/**
 * Object-generator operations slice for the Zustand store.
 *
 * The store is a scheduler and state applier only. All generator execution
 * runs through the shared worker pipeline in `modeling/generators/`.
 *
 * Two output shapes:
 *
 *  - **Single-mesh** generators produce one SceneObject with `generator`
 *    set to `{ generatorId, params }`.
 *
 *  - **Compound** generators produce a Collection whose `generatorRef`
 *    owns the authoritative params plus a `roleToObjectId` map. Each
 *    member SceneObject is a plain generated child with no ref of its
 *    own, so the generator is cleanly represented in the hierarchy as
 *    a single collapsible parent with a distinctive icon.
 *
 * Actions:
 *  - `addGenerator(id)` — snapshots generator inputs, runs the worker,
 *    then commits the created objects atomically on success.
 *  - `adjustGeneratorParams(target, nextParams)` — updates params in the
 *    store immediately, then schedules a latest-only worker rebuild for
 *    that owner. Undo is captured once per gesture by the sidebar.
 *  - `commitGeneratorEdit(target)` — gesture-end hook. No-op today;
 *    kept for symmetry and future expansion.
 */

import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import { cursorSpawnPosition } from "../modeling/cursor3d.ts";
import { executeGeneratorInWorker } from "../modeling/generators/generatorWorkerClient.ts";
import { getGenerator } from "../modeling/generators/registry.ts";
import {
  buildGeneratorRequest,
  collectReferencedObjectIds,
  type GeneratorMeshShading,
  type GeneratorRequest,
  type GeneratorResult,
} from "../modeling/generators/runtime.ts";
import type {
  CollectionGeneratorRef,
  GeneratorCompanion,
  GeneratorParams,
  GeneratorParamValue,
} from "../modeling/generators/types.ts";
import type { PrimitiveGeometry } from "../modeling/primitives.ts";
import { buildSurfaceArrayModifier } from "../modeling/surfaceOperatorModifiers.ts";
import type { SceneObject } from "../modeling/types.ts";
import {
  asLoopNormals,
  createDefaultMaterialSlots,
  createMaterialId,
  createSceneObjectId,
  EMPTY_RENDER_TRIANGLE_INDICES,
} from "../modeling/types.ts";
import { showToast } from "../ui/toastBus.ts";
import { pruneSceneSketch } from "../sketch/sceneSketch.ts";
import { createCollectionId } from "./collectionIds.ts";
import { cloneGroupTransform } from "./groupTransform.ts";
import { clearLiveEditPreview } from "./liveEditPreview.ts";
import {
  addSceneObject,
  addSceneObjects,
  patchSceneObject,
  type SceneObjectMap,
} from "./sceneObjectMap.ts";
import type {
  AppState,
  Collection,
  InteractionMode,
  ModifierOverride,
} from "./storeTypes.ts";
import { IDENTITY_GROUP_TRANSFORM } from "./storeTypes.ts";

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

// ---------------------------------------------------------------------------
// Target — which owner a generator action is addressing
// ---------------------------------------------------------------------------

/**
 * A generator lives either on a SceneObject (single-mesh output) or on a
 * Collection (compound group). Action callers disambiguate via this
 * tagged target so the store slice can centralise reads/writes.
 */
export type GeneratorTarget =
  | { kind: "object"; id: string }
  | { kind: "collection"; id: string };

/**
 * Resolve the set of generator-backed owners that depend (directly) on
 * any of the given source object ids. Pure read-only walk over scene
 * objects + collections, so it's safe to call without scheduling
 * anything — the live-drag command path captures the result once at
 * command-enter and reuses it across pointermove frames, avoiding the
 * O(generators × paramSpecs) scan per frame per dragged target.
 */
export function findDependentRebuildTargets(
  state: AppState,
  sourceIds: ReadonlySet<string>,
): GeneratorTarget[] {
  if (sourceIds.size === 0) return [];
  const targets: GeneratorTarget[] = [];

  for (const [objectId, object] of state.sceneObjects) {
    const generatorRef = object.generator;
    if (!generatorRef) continue;
    const generator = getGenerator(generatorRef.generatorId);
    if (!generator) continue;
    const referenced = collectReferencedObjectIds(
      generator.paramSpecs,
      generatorRef.params,
    );
    let hits = false;
    for (const sourceId of sourceIds) {
      if (referenced.has(sourceId)) {
        hits = true;
        break;
      }
    }
    if (hits) targets.push({ kind: "object", id: objectId });
  }

  for (const [collectionId, collection] of state.collections) {
    const generatorRef = collection.generatorRef;
    if (!generatorRef) continue;
    const generator = getGenerator(generatorRef.generatorId);
    if (!generator) continue;
    const referenced = collectReferencedObjectIds(
      generator.paramSpecs,
      generatorRef.params,
    );
    let hits = false;
    for (const sourceId of sourceIds) {
      if (referenced.has(sourceId)) {
        hits = true;
        break;
      }
    }
    if (hits) targets.push({ kind: "collection", id: collectionId });
  }

  return targets;
}

// ---------------------------------------------------------------------------
// Per-primitive shading defaults
// ---------------------------------------------------------------------------

const PRIMITIVE_GENERATOR_IDS = new Set([
  "cube",
  "plane",
  "circle",
  "sphere",
  "icosphere",
  "cylinder",
  "cone",
  "torus",
]);

// ---------------------------------------------------------------------------
// Generator-owned modifier block — replace as a unit on build / rebuild,
// preserving user-added overrides that sit before or after the block.
// ---------------------------------------------------------------------------

type GeneratorModifierOwner = NonNullable<ModifierOverride["generatorOwner"]>;

const GENERATOR_OUTPUT_OWNER: GeneratorModifierOwner = "generatorOutput";
const OBJECT_CREATE_ARRAY_OWNER: GeneratorModifierOwner = "objectCreateArray";

function isOwnedByModifierBlock(
  override: ModifierOverride,
  owner: GeneratorModifierOwner,
  replacingWithNonEmptyBlock: boolean,
): boolean {
  if (!override.generatorOwned) return false;
  if (override.generatorOwner) return override.generatorOwner === owner;
  // Legacy unscoped overrides predate `generatorOwner`. Object-create Count
  // only ever managed Array modifiers, while generator-output modifiers should
  // only absorb unscoped blocks when the generator is actively replacing them.
  if (owner === OBJECT_CREATE_ARRAY_OWNER) {
    return override.runtimeModifier?.kind === "array";
  }
  return owner === GENERATOR_OUTPUT_OWNER && replacingWithNonEmptyBlock;
}

/**
 * Pure helper: compute the next `modifierOverrides` map after replacing the
 * generator-owned modifier block for `ownerId` and `owner`. Accepts the current map
 * directly so callers can batch this into an existing `set()` call without a
 * second store write. Maintains the three-part layout:
 * user prefix | generator-owned block | user suffix.
 * Pass `modifiers=[]` to clear that owner block entirely.
 */
export function buildNextModifierOverrides(
  current: Map<string, ModifierOverride[]>,
  ownerId: string,
  modifiers: readonly BlendModifier[],
  owner: GeneratorModifierOwner = GENERATOR_OUTPUT_OWNER,
): Map<string, ModifierOverride[]> {
  const prev = current.get(ownerId) ?? [];
  const replacingWithNonEmptyBlock = modifiers.length > 0;

  let blockStart = -1;
  let blockEnd = -1;
  for (let i = 0; i < prev.length; i++) {
    if (isOwnedByModifierBlock(prev[i], owner, replacingWithNonEmptyBlock)) {
      if (blockStart === -1) blockStart = i;
      blockEnd = i;
    }
  }
  const prefix = blockStart === -1 ? prev : prev.slice(0, blockStart);
  const suffix = blockEnd === -1 ? [] : prev.slice(blockEnd + 1);

  const block: ModifierOverride[] = modifiers.map((m) => ({
    enabled: true,
    runtimeModifier: m,
    generatorOwned: true,
    generatorOwner: owner,
  }));

  const next = [...prefix, ...block, ...suffix];
  const nextMap = new Map(current);
  if (next.length === 0) {
    nextMap.delete(ownerId);
  } else {
    nextMap.set(ownerId, next);
  }
  return nextMap;
}

/**
 * Reconcile a generator-owned Array modifier on `ownerId` from an object-mode
 * panel's `count` + `offsetX/Y/Z` params. Count >= 2 installs (or updates)
 * exactly one Array modifier with `useRelativeOffset: true`; count < 2 clears
 * the generator-owned block entirely. User-added modifiers in the prefix or
 * suffix are preserved by `buildNextModifierOverrides`.
 */
export function reconcileArrayBlock(
  current: Map<string, ModifierOverride[]>,
  ownerId: string,
  params: Readonly<Record<string, number | boolean | string>>,
): Map<string, ModifierOverride[]> {
  const arrayMod = buildSurfaceArrayModifier(params);
  if (!arrayMod) {
    return buildNextModifierOverrides(
      current,
      ownerId,
      [],
      OBJECT_CREATE_ARRAY_OWNER,
    );
  }
  return buildNextModifierOverrides(
    current,
    ownerId,
    [arrayMod],
    OBJECT_CREATE_ARRAY_OWNER,
  );
}

function normalizeAppliedGeneratorOverrides(
  overrides: readonly ModifierOverride[] | undefined,
): ModifierOverride[] | undefined {
  if (!overrides || overrides.length === 0)
    return overrides ? [...overrides] : undefined;
  let changed = false;
  const next = overrides.map((override) => {
    if (!override.generatorOwned) return override;
    changed = true;
    const {
      generatorOwned: _generatorOwned,
      generatorOwner: _generatorOwner,
      ...rest
    } = override;
    return rest;
  });
  return changed ? next : [...overrides];
}

/**
 * Match the per-primitive shading defaults that `addPrimitive` uses, so an
 * object created through the generator path visually matches an object
 * created through the legacy path.
 */
function shadingForGenerator(
  generatorId: string,
): "flat" | "smooth" | "autoSmooth" {
  switch (generatorId) {
    case "cube":
    case "plane":
    case "circle":
    case "room":
    case "stairs":
    case "table":
    case "bookshelf":
    case "bed":
    case "surface_box":
    case "surface_poly":
      return "flat";
    case "cylinder":
    case "cone":
    case "lamp":
    case "ball":
    case "bowl":
    case "hat":
    case "baseball_bat":
    case "surface_cylinder":
      return "autoSmooth";
    case "sphere":
    case "icosphere":
    case "torus":
      return "smooth";
    default:
      return "flat";
  }
}

/**
 * Resolve the ObjectKind stamped on SceneObjects produced by a generator.
 * Primitives whose id matches a `PrimitiveKind` pass through directly;
 * any other generator stamps "imported" as a safe fallback.
 */
function objectKindForGenerator(
  generatorId: string,
): SceneObject["objectKind"] {
  if (PRIMITIVE_GENERATOR_IDS.has(generatorId)) {
    return generatorId as SceneObject["objectKind"];
  }
  return "imported";
}

// ---------------------------------------------------------------------------
// Naming helper
// ---------------------------------------------------------------------------

/**
 * Suffix-counter for generated object names — matches the "Cube.001" /
 * "Cube.002" convention used by `addPrimitive`. Compared
 * against both SceneObject names and Collection names so that a Room
 * collection doesn't collide with a Room-shaped SceneObject.
 */
function nextNamedSuffix(
  sceneObjects: Map<string, SceneObject>,
  collections: Map<string, Collection>,
  baseName: string,
): string {
  let count = 0;
  const matches = (name: string) =>
    name === baseName || name.startsWith(`${baseName}.`);
  for (const object of sceneObjects.values()) if (matches(object.name)) count++;
  for (const collection of collections.values())
    if (matches(collection.name)) count++;
  return `${baseName}.${String(count + 1).padStart(3, "0")}`;
}

/**
 * Pick a suffix-numbered name for a companion SceneObject. Thin wrapper
 * over `nextNamedSuffix` — kept separate so the companion-naming policy
 * can evolve independently (e.g. appending the companion's `id`) without
 * disturbing the primary suffix rules.
 */
function nextCompanionName(
  sceneObjects: Map<string, SceneObject>,
  collections: Map<string, Collection>,
  companion: GeneratorCompanion,
): string {
  return nextNamedSuffix(sceneObjects, collections, companion.name);
}

// ---------------------------------------------------------------------------
// Companion SceneObject builder — a transform-only helper object (empty)
// used when a generator declares sibling objects to spawn alongside itself.
// Mirrors the `addPrimitive` empty path in store.ts exactly so companions
// are indistinguishable from user-created empties after the fact.
// ---------------------------------------------------------------------------

function buildEmptySceneObject(
  id: string,
  name: string,
  position: [number, number, number],
  materialSlotsId: string,
): SceneObject {
  return {
    id,
    name,
    objectKind: "empty",
    position,
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    vertices: new Float32Array(),
    indices: EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(new Float32Array()),
    topology: {
      offsets: new Uint32Array([0]),
      corners: new Uint32Array(),
      edges: new Uint32Array(0),
      cornerEdges: new Uint32Array(0),
      edgeFlags: new Uint8Array(0),
    },
    uvs: new Float32Array(0),
    materialSlots: createDefaultMaterialSlots(materialSlotsId),
    shading: "flat",
  };
}

// ---------------------------------------------------------------------------
// SceneObject builders
// ---------------------------------------------------------------------------

function sceneObjectFromGeometry(
  id: string,
  name: string,
  objectKind: SceneObject["objectKind"],
  geometry: PrimitiveGeometry,
  position: [number, number, number],
  rotation: [number, number, number],
  scale: [number, number, number],
  materialSlotsId: string,
  shading: "flat" | "smooth" | "autoSmooth",
): SceneObject {
  return {
    id,
    name,
    objectKind,
    position,
    rotation,
    scale,
    vertices: geometry.vertices,
    indices: geometry.indices,
    loopNormals: geometry.loopNormals,
    topology: geometry.topology,
    uvs: geometry.uvs,
    materialSlots: createDefaultMaterialSlots(materialSlotsId),
    shading,
  };
}

function patchForRebuiltChild(
  name: string,
  geometry: PrimitiveGeometry,
  position: [number, number, number],
  rotation: [number, number, number],
  scale: [number, number, number],
  topologyVersion: number,
): Partial<SceneObject> {
  return {
    name,
    vertices: geometry.vertices,
    indices: geometry.indices,
    loopNormals: geometry.loopNormals,
    topology: geometry.topology,
    uvs: geometry.uvs,
    position,
    rotation,
    scale,
    topologyVersion,
    vertexGroups: undefined,
    skinRadii: undefined,
    baseVertices: undefined,
  };
}

// ---------------------------------------------------------------------------
// Request / scheduling helpers
// ---------------------------------------------------------------------------

function generatorTargetKey(target: GeneratorTarget): string {
  return `${target.kind}:${target.id}`;
}

function meshShadingForObject(
  object: Pick<SceneObject, "shading" | "autoSmoothAngle">,
): GeneratorMeshShading {
  return object.autoSmoothAngle == null
    ? { shading: object.shading }
    : { shading: object.shading, autoSmoothAngle: object.autoSmoothAngle };
}

function defaultShadingForGenerator(generatorId: string): GeneratorMeshShading {
  return { shading: shadingForGenerator(generatorId) };
}

interface PlannedCompanion {
  id: string;
  name: string;
  position: [number, number, number];
}

function planGeneratorCompanions(
  state: AppState,
  generatorId: string,
  companions: readonly GeneratorCompanion[] | undefined,
  cursorPos: [number, number, number],
): {
  params: GeneratorParams;
  planned: PlannedCompanion[];
  snapshotObjects: SceneObject[];
} {
  const generator = getGenerator(generatorId);
  if (!generator) {
    throw new Error(`Unknown generator "${generatorId}".`);
  }

  const params: { [key: string]: GeneratorParamValue } = {
    ...generator.defaultParams,
  };
  const planned: PlannedCompanion[] = [];
  const snapshotObjects: SceneObject[] = [];
  let namingSceneObjects = state.sceneObjects;

  for (const companion of companions ?? []) {
    if (companion.objectKind !== "empty") {
      throw new Error(
        `Generator companion "${companion.id}" on "${generatorId}" must use objectKind "empty".`,
      );
    }

    const id = createSceneObjectId();
    const name = nextCompanionName(
      namingSceneObjects,
      state.collections,
      companion,
    );
    const position: [number, number, number] = [
      cursorPos[0] + companion.offset[0],
      cursorPos[1] + companion.offset[1],
      cursorPos[2] + companion.offset[2],
    ];
    const snapshot = buildEmptySceneObject(
      id,
      name,
      position,
      createMaterialId(),
    );

    params[companion.bindTo] = id;
    planned.push({ id, name, position });
    snapshotObjects.push(snapshot);
    namingSceneObjects = addSceneObject(
      namingSceneObjects,
      snapshot,
      "system",
    ).map;
  }

  return { params, planned, snapshotObjects };
}

function commitPlannedCompanions(
  state: AppState,
  sceneObjects: SceneObjectMap,
  planned: readonly PlannedCompanion[],
): SceneObjectMap {
  let next = sceneObjects;
  for (const companion of planned) {
    next = addSceneObject(
      next,
      buildEmptySceneObject(
        companion.id,
        companion.name,
        companion.position,
        state.createMaterial(),
      ),
      "system",
    ).map;
  }
  return next;
}

interface TargetRequestBuild {
  generatorId: string;
  label: string;
  params: GeneratorParams;
  request: GeneratorRequest;
}

function buildRequestForTarget(
  state: AppState,
  target: GeneratorTarget,
): TargetRequestBuild | null {
  if (target.kind === "object") {
    const owner = state.sceneObjects.get(target.id);
    if (!owner?.generator) return null;
    const generator = getGenerator(owner.generator.generatorId);
    if (!generator) return null;
    return {
      generatorId: generator.id,
      label: generator.label,
      params: owner.generator.params,
      request: buildGeneratorRequest(
        generator.id,
        generator.paramSpecs,
        owner.generator.params,
        state.sceneObjects,
        {
          mode: "rebuild",
          meshShading: meshShadingForObject(owner),
        },
      ),
    };
  }

  const collection = state.collections.get(target.id);
  if (!collection?.generatorRef) return null;
  const generator = getGenerator(collection.generatorRef.generatorId);
  if (!generator) return null;

  const childShadingByRoleId: Record<string, GeneratorMeshShading> = {};
  for (const [roleId, objectId] of Object.entries(
    collection.generatorRef.roleToObjectId,
  )) {
    const child = state.sceneObjects.get(objectId);
    if (!child) continue;
    childShadingByRoleId[roleId] = meshShadingForObject(child);
  }

  return {
    generatorId: generator.id,
    label: generator.label,
    params: collection.generatorRef.params,
    request: buildGeneratorRequest(
      generator.id,
      generator.paramSpecs,
      collection.generatorRef.params,
      state.sceneObjects,
      {
        mode: "rebuild",
        defaultChildShading: defaultShadingForGenerator(generator.id),
        childShadingByRoleId,
      },
    ),
  };
}

function reportGeneratorTaskError(
  phase: "add" | "rebuild",
  label: string,
  error: unknown,
  dedupKey: string,
): void {
  const message = error instanceof Error ? error.message : String(error);
  showToast(
    `${phase === "add" ? "Failed to add" : "Failed to rebuild"} ${label}: ${message}`,
    {
      severity: "error",
      duration: 7000,
      dedupKey,
    },
  );
  console.error(`generator ${phase} failed for ${label}`, error);
}

function reportGeneratorShapeError(
  generatorLabel: string,
  expected: "mesh" | "group",
  actual: "mesh" | "group",
): void {
  showToast(
    `${generatorLabel} returned ${actual} output where ${expected} output was required.`,
    {
      severity: "error",
      duration: 7000,
      dedupKey: `generator-shape-${generatorLabel}`,
    },
  );
  console.error(
    `generator output shape mismatch for ${generatorLabel}: expected ${expected}, received ${actual}`,
  );
}

function applyObjectGeneratorResult(
  get: Get_,
  set: Set_,
  targetId: string,
  requestBuild: TargetRequestBuild,
  result: GeneratorResult,
): boolean {
  const state = get();
  const owner = state.sceneObjects.get(targetId);
  if (!owner?.generator) return false;
  if (owner.generator.generatorId !== requestBuild.generatorId) return false;
  if (owner.generator.params !== requestBuild.params) return false;

  if (result.output.kind !== "mesh") {
    reportGeneratorShapeError(requestBuild.label, "mesh", result.output.kind);
    return false;
  }

  const canSkipGeometry =
    result.geometrySignature != null &&
    owner.generator.geometrySignature != null &&
    result.geometrySignature === owner.generator.geometrySignature &&
    owner.vertices.length > 0;

  const patch: Partial<SceneObject> = {
    generator: {
      generatorId: requestBuild.generatorId,
      params: requestBuild.params,
      worldSpace: result.output.worldSpace,
      geometrySignature: result.geometrySignature,
    },
  };

  if (!canSkipGeometry) {
    const topologyVersion = (owner.topologyVersion ?? 0) + 1;
    patch.vertices = result.output.geometry.vertices;
    patch.indices = result.output.geometry.indices;
    patch.loopNormals = result.output.geometry.loopNormals;
    patch.topology = result.output.geometry.topology;
    patch.uvs = result.output.geometry.uvs;
    patch.topologyVersion = topologyVersion;
    patch.vertexGroups = undefined;
    patch.skinRadii = undefined;
    patch.baseVertices = undefined;
    if (result.output.worldSpace) {
      patch.position = [0, 0, 0];
      patch.rotation = [0, 0, 0];
      patch.scale = [1, 1, 1];
    }
  }

  const sceneObjects = patchSceneObject(state.sceneObjects, targetId, patch);
  const modifierOverrides = buildNextModifierOverrides(
    state.modifierOverrides,
    targetId,
    result.output.generatorModifiers ?? [],
  );
  const sketch =
    patch.topologyVersion !== undefined
      ? pruneSceneSketch(state.sketch, {
          topologyBumped: new Map([[targetId, patch.topologyVersion]]),
        })
      : state.sketch;
  set({ sceneObjects, modifierOverrides, sketch });
  return true;
}

function applyCollectionGeneratorResult(
  get: Get_,
  set: Set_,
  targetId: string,
  requestBuild: TargetRequestBuild,
  result: GeneratorResult,
): boolean {
  const state = get();
  const collection = state.collections.get(targetId);
  if (!collection?.generatorRef) return false;
  if (collection.generatorRef.generatorId !== requestBuild.generatorId)
    return false;
  if (collection.generatorRef.params !== requestBuild.params) return false;

  const generator = getGenerator(requestBuild.generatorId);
  if (!generator) return false;

  if (result.output.kind !== "group") {
    reportGeneratorShapeError(requestBuild.label, "group", result.output.kind);
    return false;
  }

  const prevRoleMap = collection.generatorRef.roleToObjectId;
  let sceneObjects = state.sceneObjects;
  const nextRoleMap: { [role: string]: string } = {};
  const topologyBumped = new Map<string, number>();

  for (const childSpec of result.output.children) {
    const localPos = [...childSpec.localTransform.position] as [
      number,
      number,
      number,
    ];
    const rotation = [...childSpec.localTransform.rotation] as [
      number,
      number,
      number,
    ];
    const scale = [...childSpec.localTransform.scale] as [
      number,
      number,
      number,
    ];

    const existingId = prevRoleMap[childSpec.roleId];
    const existing = existingId ? sceneObjects.get(existingId) : undefined;
    if (existing) {
      const topologyVersion = (existing.topologyVersion ?? 0) + 1;
      sceneObjects = patchSceneObject(
        sceneObjects,
        existingId,
        patchForRebuiltChild(
          childSpec.name,
          childSpec.geometry,
          localPos,
          rotation,
          scale,
          topologyVersion,
        ),
      );
      topologyBumped.set(existingId, topologyVersion);
      nextRoleMap[childSpec.roleId] = existingId;
      continue;
    }

    const childId = createSceneObjectId();
    sceneObjects = addSceneObject(
      sceneObjects,
      sceneObjectFromGeometry(
        childId,
        childSpec.name,
        childSpec.objectKind,
        childSpec.geometry,
        localPos,
        rotation,
        scale,
        state.createMaterial(),
        shadingForGenerator(generator.id),
      ),
      "system",
    ).map;
    nextRoleMap[childSpec.roleId] = childId;
  }

  const obsolete: string[] = [];
  for (const [roleId, childId] of Object.entries(prevRoleMap)) {
    if (!(roleId in nextRoleMap)) obsolete.push(childId);
  }
  if (obsolete.length > 0) {
    const next = new Map(sceneObjects);
    for (const childId of obsolete) next.delete(childId);
    sceneObjects = next;
  }

  const collections = new Map(state.collections);
  collections.set(targetId, {
    ...collection,
    transform: cloneGroupTransform(collection.transform),
    objectIds: new Set<string>(Object.values(nextRoleMap)),
    generatorRef: {
      generatorId: generator.id,
      params: requestBuild.params,
      roleToObjectId: nextRoleMap,
    },
  });

  let selectedObjectTargets = state.selectedObjectTargets;
  if (obsolete.length > 0) {
    const obsoleteSet = new Set(obsolete);
    const filtered = selectedObjectTargets.filter(
      (candidate) =>
        !(candidate.kind === "sceneObject" && obsoleteSet.has(candidate.id)),
    );
    if (filtered.length !== selectedObjectTargets.length) {
      selectedObjectTargets = filtered;
    }
  }

  let nextVisibility: Map<string, boolean> | null = null;
  if (generator.hideableParts && generator.hideableParts.length > 0) {
    for (const part of generator.hideableParts) {
      const partId = nextRoleMap[part.roleId];
      if (!partId) continue;
      const partVisible = state.visibility.get(partId) ?? true;
      if (partVisible || !part.dependentRoles) continue;
      for (const roleId of part.dependentRoles(requestBuild.params)) {
        const newChildId = nextRoleMap[roleId];
        if (!newChildId) continue;
        if (prevRoleMap[roleId] === newChildId) continue;
        if (!nextVisibility) nextVisibility = new Map(state.visibility);
        nextVisibility.set(newChildId, false);
      }
    }
  }

  const patch: Partial<AppState> = {
    sceneObjects,
    collections,
    selectedObjectTargets,
  };
  if (topologyBumped.size > 0 || obsolete.length > 0) {
    patch.sketch = pruneSceneSketch(state.sketch, {
      removedObjectIds: new Set(obsolete),
      topologyBumped,
    });
  }
  if (nextVisibility) patch.visibility = nextVisibility;
  set(patch);
  return true;
}

function applyTargetGeneratorResult(
  get: Get_,
  set: Set_,
  target: GeneratorTarget,
  requestBuild: TargetRequestBuild,
  result: GeneratorResult,
): boolean {
  return target.kind === "object"
    ? applyObjectGeneratorResult(get, set, target.id, requestBuild, result)
    : applyCollectionGeneratorResult(get, set, target.id, requestBuild, result);
}

function updateTargetGeneratorParams(
  get: Get_,
  set: Set_,
  target: GeneratorTarget,
  nextParams: GeneratorParams,
): boolean {
  const state = get();
  const params: GeneratorParams = { ...nextParams };

  if (target.kind === "object") {
    const owner = state.sceneObjects.get(target.id);
    if (!owner?.generator) return false;
    const sceneObjects = patchSceneObject(state.sceneObjects, target.id, {
      generator: {
        ...owner.generator,
        params,
      },
    });
    set({ sceneObjects });
    return true;
  }

  const collection = state.collections.get(target.id);
  if (!collection?.generatorRef) return false;
  const collections = new Map(state.collections);
  collections.set(target.id, {
    ...collection,
    generatorRef: {
      ...collection.generatorRef,
      params,
    },
  });
  set({ collections });
  return true;
}

// ---------------------------------------------------------------------------
// Factory — the slice
// ---------------------------------------------------------------------------

export function createGeneratorOps(set: Set_, get: Get_) {
  const rebuildStates = new Map<
    string,
    { revision: number; running: boolean }
  >();

  function invalidateScheduledRebuild(target: GeneratorTarget): void {
    const key = generatorTargetKey(target);
    const state = rebuildStates.get(key);
    if (!state) return;
    state.revision += 1;
    if (!state.running) rebuildStates.delete(key);
  }

  async function drainScheduledRebuilds(
    target: GeneratorTarget,
    key: string,
  ): Promise<void> {
    const queueState = rebuildStates.get(key);
    if (!queueState || queueState.running) return;
    queueState.running = true;

    let lastAttemptRevision = queueState.revision;

    try {
      while (true) {
        const latestQueued = rebuildStates.get(key);
        if (!latestQueued) return;
        lastAttemptRevision = latestQueued.revision;

        const requestBuild = buildRequestForTarget(get(), target);
        if (!requestBuild) return;

        let result: GeneratorResult;
        try {
          result = await executeGeneratorInWorker(requestBuild.request);
        } catch (error) {
          const current = rebuildStates.get(key);
          if (!current) return;
          if (current.revision !== lastAttemptRevision) continue;
          reportGeneratorTaskError(
            "rebuild",
            requestBuild.label,
            error,
            `generator-rebuild-${key}`,
          );
          return;
        }

        const current = rebuildStates.get(key);
        if (!current) return;
        if (current.revision !== lastAttemptRevision) continue;
        if (!applyTargetGeneratorResult(get, set, target, requestBuild, result))
          return;

        const afterApply = rebuildStates.get(key);
        if (!afterApply || afterApply.revision === lastAttemptRevision) return;
      }
    } finally {
      const current = rebuildStates.get(key);
      if (current) {
        current.running = false;
        if (current.revision !== lastAttemptRevision) {
          void drainScheduledRebuilds(target, key);
        } else {
          rebuildStates.delete(key);
        }
      }
    }
  }

  function scheduleTargetRebuild(target: GeneratorTarget): void {
    const key = generatorTargetKey(target);
    const state = rebuildStates.get(key) ?? { revision: 0, running: false };
    state.revision += 1;
    rebuildStates.set(key, state);
    if (!state.running) {
      void drainScheduledRebuilds(target, key);
    }
  }

  return {
    addGenerator: (generatorId: string) => {
      const generator = getGenerator(generatorId);
      if (!generator) {
        console.warn(`addGenerator: unknown generator id "${generatorId}"`);
        return;
      }

      const state = get();
      if (state.workspaceMode !== "object") {
        state.setWorkspaceMode("object");
      }

      const cursorPos = cursorSpawnPosition(state.cursor3D);
      const generatorShading = shadingForGenerator(generator.id);

      let params: GeneratorParams;
      let plannedCompanions: PlannedCompanion[];
      let snapshotCompanions: SceneObject[];
      try {
        const plan = planGeneratorCompanions(
          state,
          generator.id,
          generator.companions,
          cursorPos,
        );
        params = plan.params;
        plannedCompanions = plan.planned;
        snapshotCompanions = plan.snapshotObjects;
      } catch (error) {
        reportGeneratorTaskError(
          "add",
          generator.label,
          error,
          `generator-add-${generator.id}`,
        );
        return;
      }

      const request = buildGeneratorRequest(
        generator.id,
        generator.paramSpecs,
        params,
        state.sceneObjects,
        {
          mode: "build",
          meshShading: defaultShadingForGenerator(generator.id),
          defaultChildShading: defaultShadingForGenerator(generator.id),
          extraSceneObjects: snapshotCompanions,
        },
      );

      void (async () => {
        let result: GeneratorResult;
        try {
          result = await executeGeneratorInWorker(request);
        } catch (error) {
          reportGeneratorTaskError(
            "add",
            generator.label,
            error,
            `generator-add-${generator.id}`,
          );
          return;
        }

        const latest = get();
        latest.pushUndo();

        const sceneObjects = commitPlannedCompanions(
          latest,
          latest.sceneObjects,
          plannedCompanions,
        );

        if (result.output.kind === "mesh") {
          const spawnPos: [number, number, number] = result.output.worldSpace
            ? [0, 0, 0]
            : cursorPos;

          const object = sceneObjectFromGeometry(
            createSceneObjectId(),
            nextNamedSuffix(sceneObjects, latest.collections, generator.label),
            objectKindForGenerator(generator.id),
            result.output.geometry,
            spawnPos,
            [0, 0, 0],
            [1, 1, 1],
            latest.createMaterial(),
            generatorShading,
          );
          object.generator = {
            generatorId: generator.id,
            params,
            worldSpace: result.output.worldSpace,
            geometrySignature: result.geometrySignature,
          };

          const modifierOverrides = result.output.generatorModifiers?.length
            ? buildNextModifierOverrides(
                latest.modifierOverrides,
                object.id,
                result.output.generatorModifiers,
              )
            : latest.modifierOverrides;

          clearLiveEditPreview();
          set({
            sceneObjects: addSceneObject(sceneObjects, object, "system").map,
            selectedObjectTargets: [{ kind: "sceneObject", id: object.id }],
            mode: "transform" as InteractionMode,
            editMode: null,
            modifierOverrides,
          });
          return;
        }

        const childObjects: SceneObject[] = [];
        const roleToObjectId: { [role: string]: string } = {};
        for (const childSpec of result.output.children) {
          const childId = createSceneObjectId();
          childObjects.push(
            sceneObjectFromGeometry(
              childId,
              childSpec.name,
              childSpec.objectKind,
              childSpec.geometry,
              [...childSpec.localTransform.position] as [
                number,
                number,
                number,
              ],
              [...childSpec.localTransform.rotation] as [
                number,
                number,
                number,
              ],
              [...childSpec.localTransform.scale] as [number, number, number],
              latest.createMaterial(),
              generatorShading,
            ),
          );
          roleToObjectId[childSpec.roleId] = childId;
        }

        const generatorRef: CollectionGeneratorRef = {
          generatorId: generator.id,
          params,
          roleToObjectId,
        };
        const collectionId = createCollectionId();
        const collections = new Map(latest.collections);
        collections.set(collectionId, {
          id: collectionId,
          name: nextNamedSuffix(
            sceneObjects,
            latest.collections,
            result.output.parent.name,
          ),
          transform: {
            position: [...cursorPos] as [number, number, number],
            rotation: [...IDENTITY_GROUP_TRANSFORM.rotation] as [
              number,
              number,
              number,
              number,
            ],
            scale: [...IDENTITY_GROUP_TRANSFORM.scale] as [
              number,
              number,
              number,
            ],
          },
          objectIds: new Set(childObjects.map((child) => child.id)),
          generatorRef,
        });

        clearLiveEditPreview();
        set({
          sceneObjects: addSceneObjects(sceneObjects, childObjects, "system")
            .map,
          collections,
          selectedObjectTargets: [{ kind: "collection", id: collectionId }],
          mode: "transform" as InteractionMode,
          editMode: null,
        });
      })();
    },

    adjustGeneratorParams: (
      target: GeneratorTarget,
      nextParams: GeneratorParams,
    ) => {
      if (!updateTargetGeneratorParams(get, set, target, nextParams)) return;
      scheduleTargetRebuild(target);
    },

    commitGeneratorEdit: (_target: GeneratorTarget) => {
      // Undo is already captured pre-gesture by the sidebar via pushUndo().
      // Hook retained for symmetry and future expansion.
    },

    applyGenerator: (target: GeneratorTarget) => {
      invalidateScheduledRebuild(target);

      const state = get();
      state.pushUndo();

      if (target.kind === "object") {
        const owner = state.sceneObjects.get(target.id);
        if (!owner?.generator) return;
        const sceneObjects = patchSceneObject(state.sceneObjects, target.id, {
          generator: undefined,
        });
        const modifierOverrides = new Map(state.modifierOverrides);
        const normalized = normalizeAppliedGeneratorOverrides(
          modifierOverrides.get(target.id),
        );
        if (!normalized || normalized.length === 0) {
          modifierOverrides.delete(target.id);
        } else {
          modifierOverrides.set(target.id, normalized);
        }
        set({ sceneObjects, modifierOverrides });
        return;
      }

      const collection = state.collections.get(target.id);
      if (!collection?.generatorRef) return;
      const collections = new Map(state.collections);
      const { generatorRef: _generatorRef, ...bakedCollection } = collection;
      collections.set(target.id, bakedCollection);

      const modifierOverrides = new Map(state.modifierOverrides);
      for (const objectId of collection.objectIds) {
        const normalized = normalizeAppliedGeneratorOverrides(
          modifierOverrides.get(objectId),
        );
        if (!normalized || normalized.length === 0) {
          modifierOverrides.delete(objectId);
        } else {
          modifierOverrides.set(objectId, normalized);
        }
      }

      set({ collections, modifierOverrides });
    },

    /**
     * Walk every generator-backed owner (SceneObjects with a `generator`
     * ref, Collections with a `generatorRef`) and rebuild any whose
     * params currently reference `sourceId`.
     *
     * O(generators × refs) — trivially cheap at realistic scene sizes.
     * Upgrade to a reverse index only if profiling shows it's hot.
     *
     * Live-drag callers should prefer `scheduleDependentRebuildTargets`
     * with a list pre-resolved at command-enter — the per-frame scan
     * shows up in profiles on scenes with many generators.
     */
    rebuildDependentGenerators: (sourceId: string) => {
      if (!sourceId) return;
      const targets = findDependentRebuildTargets(get(), new Set([sourceId]));
      for (const target of targets) scheduleTargetRebuild(target);
    },

    /**
     * Schedule rebuilds for an already-resolved dependent set. Used by
     * the live transform command path which captures the dependent
     * targets once at command-enter so per-frame work stays O(targets)
     * instead of O(generators).
     */
    scheduleDependentRebuildTargets: (
      targets: ReadonlyArray<GeneratorTarget>,
    ) => {
      for (const target of targets) scheduleTargetRebuild(target);
    },
  };
}
